import React, { useEffect, useState, useRef } from 'react';
import { Select, Button, Row, Col, Spin, DatePicker } from 'antd';
import styled from 'styled-components';
import { restApi } from '../../../constants/api';
import { PrinterOutlined, RedoOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

const FilterContainer = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
`;
const cellStyle: React.CSSProperties = {
  padding: '10px',
  border: '1px solid #ccc',
  textAlign: 'center',
  verticalAlign: 'middle',
};

const AllEmployeeDetails = () => {
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef: any = useRef();

  const [selected, setSelected] = useState({
    department: undefined,
    user: undefined,
    designation: undefined,
    startDate: undefined,
    endDate: undefined,
  });

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [deptRes, userRes, desigRes]: any = await Promise.all([
          restApi('/api/departments', 'GET'),
          restApi('/api/users/dropdown', 'GET'),
          restApi('/api/user-types', 'GET'),
        ]);
        setDepartments(deptRes?.data || []);
        setUsers(userRes?.data || []);
        setDesignations(desigRes?.data || []);
      } catch (error) {
        console.error("Error fetching filters:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFilters();
  }, []);

  const handleSearch = async () => {
    try {
      const body = {
        current_date: dayjs().format('YYYY-MM-DD'),
        user_id: selected.user,
        user_type_id: selected.designation,
        department_id: selected.department,
        start_date: selected.startDate,
        end_date: selected.endDate,
      };
      const res: any = await restApi('/api/reports/employee-details', 'POST', body);
      setEmployeeData(res.data);
    } catch (error) {
      console.error("Error fetching employee details:", error);
    }
  };

  const handleReset = () => {
    setSelected({
      department: undefined,
      user: undefined,
      designation: undefined,
      startDate: undefined,
      endDate: undefined,
    });
    setEmployeeData([]);
  };

  const handlePrint = () => {
    const printContent: any = printRef.current;
    const newWin: any = window.open('', '', 'width=1000,height=800');
    newWin.document.write('<html><head><title>Print</title>');
    newWin.document.write('<style>table, th, td { border: 1px solid black; border-collapse: collapse; padding: 4px; font-size: 12px; }</style>');
    newWin.document.write('</head><body>');
    newWin.document.write(printContent.innerHTML);
    newWin.document.write('</body></html>');
    newWin.document.close();
    newWin.print();
  };

  const groupByDepartment = (data: any[]) => {
    const grouped: any = {};
    data.forEach(emp => {
      if (!grouped[emp.department_name]) {
        grouped[emp.department_name] = [];
      }
      grouped[emp.department_name].push(emp);
    });
    return grouped;
  };

  const groupedData = groupByDepartment(employeeData);

  return (
    <>
      <FilterContainer>
        {loading ? (
          <Spin size="large" />
        ) : (
          <Row gutter={[16, 16]}>
            {
              localStorage.getItem("role") === "SUPERADMIN" && (
                <Col xs={24} md={6}>
              <Select
                placeholder="Select Department"
                value={selected.department}
                onChange={(value) => setSelected(prev => ({ ...prev, department: value }))}
                style={{ width: '100%' }}
                allowClear
              >
                {departments.map((d: any) => (
                  <Option key={d.id} value={d.id}>{d.name}</Option>
                ))}
              </Select>
            </Col>
              )
            }
            

            <Col xs={24} md={6}>
              <Select
                placeholder="Select Designation"
                value={selected.designation}
                onChange={(value) => setSelected(prev => ({ ...prev, designation: value }))}
                style={{ width: '100%' }}
                allowClear
              >
                {designations.map((d: any) => (
                  <Option key={d.id} value={d.id}>{d.type_name}</Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} md={6}>
              <Select
                placeholder="Select User"
                value={selected.user}
                onChange={(value) => setSelected(prev => ({ ...prev, user: value }))}
                style={{ width: '100%' }}
                allowClear
              >
                {users.map((u: any) => (
                  <Option key={u.id} value={u.id}>{u.name}</Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} md={6}>
              <RangePicker
                style={{ width: '100%' }}
                onChange={(dates) => {
                  setSelected((prev: any) => ({
                    ...prev,
                    startDate: dates?.[0]?.format('YYYY-MM-DD'),
                    endDate: dates?.[1]?.format('YYYY-MM-DD'),
                  }));
                }}
                value={
                  selected.startDate && selected.endDate
                    ? [dayjs(selected.startDate), dayjs(selected.endDate)]
                    : null
                }
              />
            </Col>

            <Col xs={24} style={{ textAlign: 'right' }}>
              <Button style={{ background: "#77d6c9", color: "black" }}
                icon={<SearchOutlined style={{ color: 'white', fontSize: "17px" }} />} onClick={handleSearch}>Search</Button> &nbsp;
              <Button style={{ background: "#77d6c9", color: "black" }}
                icon={<RedoOutlined style={{ color: 'white', fontSize: "17px" }} />} onClick={handleReset}>Reset</Button>
            </Col>
          </Row>
        )}
      </FilterContainer>

      {employeeData.length > 0 && (
        <>
          <div style={{ margin: '20px 0', textAlign: 'right' }}>
            <Button style={{ background: "#77d6c9", color: "black" }}
              icon={<PrinterOutlined style={{ color: 'white', fontSize: "17px" }} />} onClick={handlePrint}>Print</Button>
          </div>

          <div ref={printRef} style={{ background: 'white', padding: 16 }}>
            <h3 style={{ textAlign: 'center', textTransform: 'uppercase', marginBottom: 20 }}>
              Details of Employees of Zilha Sainik Welfare Office Kolhapur 
            </h3>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, sans-serif' }} border={1}>
              <thead style={{ backgroundColor: '#76d5c8', color: '#000' }}>
                <tr>
                  {[
                    'SR NO', 'MR/MRS', 'NAME', 'DESIGNATION', 'CODE', 'ADDRESS', 'Mobile No', 'Adhar No',
                    'PAN No', 'Bank Name', 'Branch IFSC Code', 'A/C No', 'NEAREST POLICE STN',
                    'DT OF INITIAL JOINING', 'DT OF CURRENT SERVICE', 'END OF SERVICE'
                  ].map((header, index) => (
                    <th key={index} style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedData).map(([department, emps]: any, deptIndex) => (
                  <React.Fragment key={`dept-${deptIndex}`}>
                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                      <td colSpan={16} style={{ fontWeight: 'bold', padding: '10px', textAlign: 'left' }}>
                        {department}
                      </td>
                    </tr>
                    {emps.map((emp: any, index: number) => (
                      <tr
                        key={`emp-${deptIndex}-${index}`}
                        style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9', transition: 'background-color 0.3s' }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e0f7fa')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9f9f9')}
                      >
                        <td style={cellStyle}>{index + 1}</td>
                        <td style={cellStyle}>{emp.prefixName}</td>
                        <td style={cellStyle}>{emp.user_name || ''}</td>
                        <td style={cellStyle}>{emp.designation_name || ''}</td>
                        <td style={cellStyle}>{emp.code || ''}</td>
                        <td style={{ ...cellStyle, textAlign: 'left', minWidth: '180px', whiteSpace: 'pre-wrap' }}>{emp.address || ''}</td>
                        <td style={cellStyle}>{emp.mobileNo || ''}</td>
                        <td style={cellStyle}>{emp.adharnumber || ''}</td>
                        <td style={cellStyle}>{emp.pannumber || ''}</td>
                        <td style={cellStyle}>{emp.bankName || ''}</td>
                        <td style={cellStyle}>{emp.branchIfscCode || ''}</td>
                        <td style={cellStyle}>{emp.accountNumber || ''}</td>
                        <td style={cellStyle}>{emp.police_station || ''}</td>
                        <td style={cellStyle}>{emp.initial_joining_date ? dayjs(emp.initial_joining_date).format('DD MMM YYYY') : ''}</td>
                        <td style={cellStyle}>
                          {emp.start_date && emp.end_date
                            ? `${dayjs(emp.start_date).format('DD MMM YYYY')} – ${dayjs(emp.end_date).format('DD MMM YYYY')}`
                            : ''}
                        </td>
                        <td style={cellStyle}>{emp.end_date ? dayjs(emp.end_date).format('DD MMM YYYY') : ''}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* <table style={{ width: '100%', borderCollapse: 'collapse'}} border={1}>
              <thead style={{backgroundColor:"#76d5c8",}}>
                <tr>
                  <th>SR NO</th>
                  <th>MR/MRS</th>
                  <th>NAME</th>
                  <th>DESIGNATION</th>
                  <th>CODE</th>
                  <th>ADDRESS</th>
                  <th>Mobile No</th>
                  <th>Adhar No</th>
                  <th>PAN No</th>
                  <th>Bank Name</th>
                  <th>Branch IFSC Code</th>
                  <th>A/C No</th>
                  <th>NEAREST POLICE STN</th>
                  <th>DT OF INITIAL JOINING</th>
                  <th>DT OF CURRENT SERVICE</th>
                  <th>END OF SERVICE</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedData).map(([department, emps]: any, deptIndex) => (
                  <>
                    <tr key={`dept-${deptIndex}`}>
                      <td colSpan={16} style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                        {department}
                      </td>
                    </tr>
                    {emps.map((emp: any, index: number) => (
                      <tr key={`emp-${deptIndex}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{emp.prefixName}</td>
                        <td>{emp.user_name || ''}</td>
                        <td>{emp.designation_name || ''}</td>
                        <td>{emp.code || ''}</td>
                        <td>{emp.address || ''}</td>
                        <td>{emp.mobileNo || ''}</td>
                        <td>{emp.adharnumber || ''}</td>
                        <td>{emp.pannumber || ''}</td>
                        <td>{emp.bankName || ''}</td>
                        <td>{emp.branchIfscCode || ''}</td>
                        <td>{emp.accountNumber || ''}</td>
                        <td>{emp.police_station || ''}</td>
                        <td>{emp.initial_joining_date ? dayjs(emp.initial_joining_date).format('DD MMM YYYY') : ''}</td>
                        <td>
                          {emp.start_date && emp.end_date
                            ? `${dayjs(emp.start_date).format('DD MMM YYYY')} – ${dayjs(emp.end_date).format('DD MMM YYYY')}`
                            : ''}
                        </td>
                        <td>{emp.end_date ? dayjs(emp.end_date).format('DD MMM YYYY') : ''}</td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table> */}
          </div>
        </>
      )}
    </>
  );
};

export default AllEmployeeDetails;
